import type { FC, PropsWithChildren } from 'react'
import { Suspense, lazy, useMemo } from 'react'

import { faThumbTack } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { cn } from '@/utils'

const Popover = lazy(() =>
  import('@/components/interactives/Popover.jsx').then((m) => ({
    default: m.Popover,
  })),
)
const PopoverContent = lazy(() =>
  import('@/components/interactives/Popover.jsx').then((m) => ({
    default: m.PopoverContent,
  })),
)
const PopoverTrigger = lazy(() =>
  import('@/components/interactives/Popover.jsx').then((m) => ({
    default: m.PopoverTrigger,
  })),
)

type StartTimePopoverProps = {
  open: boolean
  readonly?: boolean
  setOpen: (isOpen: boolean) => void
  isHardStart: boolean
  onSave: () => void
  onCancel: () => void
  timeClassName?: string
  formattedTime: string
}

export const StartTimePopover: FC<PropsWithChildren<StartTimePopoverProps>> = ({
  open,
  readonly,
  isHardStart,
  setOpen,
  onSave,
  onCancel,
  timeClassName,
  formattedTime,
  children,
}) => {
  const TriggerButton = useMemo(() => {
    return (
      <button
        type="button"
        disabled={readonly}
        onClick={() => {
          if (readonly) return
          setOpen(true)
        }}
        className={cn(
          'min-h-max max-h-full h-6 px-1.5 w-full rounded-sm bg-black/70 hover:enabled:bg-gray-600/40 space-x-1',
          'disabled:pointer-events-none',
          open && 'ring-1 ring-white',
        )}
      >
        {isHardStart && (
          <span
            className="text-xs"
            style={{ verticalAlign: '0.05em' }}
          >
            <FontAwesomeIcon
              icon={faThumbTack}
              size="xs"
            />
          </span>
        )}
        <span
          className={cn(
            'tabular-nums text-sm whitespace-nowrap',
            timeClassName,
          )}
        >
          {formattedTime}
        </span>
      </button>
    )
  }, [formattedTime, isHardStart, open, readonly, setOpen, timeClassName])

  // When not open yet, render just the plain button; clicking it sets open=true, which then mounts the lazy Popover.
  if (!open) return TriggerButton

  // When open, mount the Popover lazily and control it via `open` state
  return (
    <Suspense fallback={TriggerButton}>
      <Popover
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        onOpenChange={setOpen}
      >
        <PopoverTrigger
          asChild={true}
          disabled={readonly}
        >
          {TriggerButton}
        </PopoverTrigger>
        <PopoverContent
          onSave={onSave}
          onCancel={onCancel}
        >
          {children}
        </PopoverContent>
      </Popover>
    </Suspense>
  )
}
